/**
 * Search Term Editor Component
 *
 * Edit AI-generated or manual search terms for criteria matching.
 */

import { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface SearchTermEditorProps {
  terms: string[];
  onChange: (terms: string[]) => void;
}

export function SearchTermEditor({ terms, onChange }: SearchTermEditorProps) {
  const [newTerm, setNewTerm] = useState('');

  const handleAddTerm = () => {
    const trimmed = newTerm.trim();
    if (trimmed && !terms.includes(trimmed)) {
      onChange([...terms, trimmed]);
      setNewTerm('');
    }
  };

  const handleRemoveTerm = (index: number) => {
    const newTerms = [...terms];
    newTerms.splice(index, 1);
    onChange(newTerms);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTerm();
    }
  };

  return (
    <div className="space-y-3">
      {/* Terms display */}
      <div className="min-h-[80px] bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
        {terms.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            No search terms defined. Click "Generate with AI" or add terms manually.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {terms.map((term, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full px-3 py-1 text-sm text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
              >
                {term}
                <button
                  type="button"
                  onClick={() => handleRemoveTerm(i)}
                  className="text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 ml-1"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Add new term */}
      <div className="flex gap-2">
        <input
          type="text"
          value={newTerm}
          onChange={e => setNewTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a search term..."
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        />
        <button
          type="button"
          onClick={handleAddTerm}
          disabled={!newTerm.trim()}
          className="inline-flex items-center gap-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-0"
        >
          <Plus size={16} />
          Add
        </button>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Search terms help the AI find relevant information in patient medical histories. Include
        synonyms, abbreviations, and related terms.
      </p>
    </div>
  );
}
